import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormatNumber } from 'core/hooks/useFormatNumber'
import { StoreTrajectoryContext } from '../../state/store-trajectory-context'
import AccountCard from './account-card'

export const sumSalesChannelPercents = (percents: SalesChannelsPercents) =>
  Math.round(((percents.physical ?? 0) + (percents.digital ?? 0) + (percents.decapro ?? 0)) * 10) / 10

export type SalesChannelsPercents = {
  physical: number
  digital: number
  decapro: number
}

type CardSalesChannelsProps = {
  initialPercents: SalesChannelsPercents
}

const CardSalesChannels = ({ initialPercents }: CardSalesChannelsProps) => {
  const { t } = useTranslation()
  const formatNumber = useFormatNumber()
  const {
    forecastInstoreGmv,
    setForecastInstorePhysical,
    setForecastInstoreDigital,
    setForecastInstoreDecapro,
    setSalesChannelsPercentTotal
  } = useContext(StoreTrajectoryContext)
  const isDisabled = forecastInstoreGmv == null || forecastInstoreGmv === 0

  const [percents, setPercents] = useState(initialPercents)

  const percentageTotal = sumSalesChannelPercents(percents)
  const isTotalMisallocated = !isDisabled && percentageTotal !== 100

  useEffect(() => {
    setSalesChannelsPercentTotal(percentageTotal)
  }, [percentageTotal, setSalesChannelsPercentTotal])

  useEffect(() => {
    if (isDisabled) {
      return
    }
    setForecastInstorePhysical(amountFromPercent(percents.physical))
    setForecastInstoreDigital(amountFromPercent(percents.digital))
    setForecastInstoreDecapro(amountFromPercent(percents.decapro))
  }, [forecastInstoreGmv, isDisabled])

  const amountFromPercent = (percent: number | null) =>
    !isDisabled && percent != null ? (percent * forecastInstoreGmv) / 100 : 0

  const updatePercentAndValue =
    <K extends keyof SalesChannelsPercents>(propsKey: K, amountSetter: (value: number) => void) =>
    (newPercent: number) => {
      setPercents(prev => ({ ...prev, [propsKey]: newPercent }))
      amountSetter(amountFromPercent(newPercent))
    }

  return (
    <div className="mg-instore-gmv-segment-card" data-testid="instore-sales-channels">
      <div className="mg-instore-gmv-segment-card-header">
        <div className="mg-instore-gmv-segment-card-header-title vp-subtitle-m">
          {t('general.trajectory.store.edit.instore_gmv_segment.sales_channels.title')}
        </div>
        <div
          className={`mg-instore-gmv-segment-card-header-total vp-body-l ${isTotalMisallocated ? 'mg-instore-gmv-segment-card-header-total-error' : ''}`}
          data-testid="card-sales-channels-total"
        >
          {formatNumber(percentageTotal / 100, '%', {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
            signDisplay: 'never'
          })}
        </div>
      </div>

      <AccountCard
        title={t('general.trajectory.store.edit.instore_gmv_segment.sales_channels.physical')}
        amount={amountFromPercent(percents.physical)}
        percent={percents.physical ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('physical', setForecastInstorePhysical)}
      />

      <AccountCard
        title={t('general.trajectory.store.edit.instore_gmv_segment.sales_channels.digital')}
        amount={amountFromPercent(percents.digital)}
        percent={percents.digital ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('digital', setForecastInstoreDigital)}
      />

      <AccountCard
        title={t('general.trajectory.store.edit.instore_gmv_segment.sales_channels.decapro')}
        amount={amountFromPercent(percents.decapro)}
        percent={percents.decapro ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('decapro', setForecastInstoreDecapro)}
      />
    </div>
  )
}

export default CardSalesChannels
